import Vue from "vue";
import VueRouter from "vue-router";
import multiguard from "vue-router-multiguard";
import Home from "@/views/Home";
import Reports from "@/views/reports/Reports";
import UserProfile from "@/views/profile/UserProfile";
import Customers from "@/views/customers/Customers";
import Customer from "@/views/customers/Customer";
import CustomerCreate from "@/views/customers/CustomerCreate";
import EmployerCreate from "@/views/employers/EmployerCreate";
import CustomerEdit from "@/views/customers/CustomerEdit";
import CreateProject from "@/views/projects/CreateProject";
import CreateRole from "@/views/roles/RoleCreate";
import EditRole from "@/views/roles/RoleEdit";
import Employers from "@/views/employers/Employers";
import EmployerEdit from "@/views/employers/EmployerEdit";
import EditProject from "@/views/projects/EditProject";
import SwitchRole from "@/components/auth/SwitchRole";
import ProjectsList from "@/views/projects/ProjectsList";
import RoleList from "@/views/roles/RoleList";
import Login from "@/components/auth/Login";
import GoogleAuth from "@/components/auth/GoogleAuth";
import MobileLogin from "@/components/auth/MobileLogin";
import ForgotPassword from "@/components/auth/ForgotPassword";
import ProjectInfo from "@/views/projects/ProjectInfo";
import TaskInfo from "@/views/tasks/TaskInfo";
import Calendar from "@/views/calendar/Calendar";
import InvoicesList from "@/views/invoices/InvoicesList";
import InvoiceList from "@/views/invoices/InvoicesList";
import CreateInvoice from "@/views/invoices/InvoiceCreate";
import CreateInvoiceProject from "@/views/invoices/InvoiceCreateProject";
import EditInvoice from "@/views/invoices/EditInvoice";
import Page403 from "@/views/errors/403.vue";
import Page404 from "@/views/errors/404.vue";
import Settings from "@/views/settings/Settings.vue";
import TasksList from "@/views/tasks/TasksList.vue";

const ifNotAuthenticated = (to, from, next) => {
  if (!localStorage.getItem("access_token")) {
    next();
    return;
  }
  next("/");
};

const ifAuthenticated = (to, from, next) => {
  if (localStorage.getItem("access_token")) {
    next();
    return;
  }
  next("/login");
};

const hasPermission = (requiredPermission, userPermissions) => {
  return userPermissions.includes(requiredPermission);
};

const checkPermission = (requiredPermission) => (to, from, next) => {
  const userPermissions = JSON.parse(localStorage.getItem("permissions")) || [];
  if (userPermissions.length === 0) {
  next({ path: '/switch-role', query: { id: Date.now() } });
  return;
}


  if (hasPermission(requiredPermission, userPermissions)) {
    next();
    return;
  } else {

    next('/error');
  }
};

const projectViewPermission = checkPermission('project.view');
const customerInfo = checkPermission('customer.view');
const taskInfo = checkPermission('task.view');
const projectsCreatePermission = checkPermission('project.create');
const invoicePermission = checkPermission('invoice.list');
const SettingsPermission = checkPermission('setting.company_settings') || checkPermission('setting.project_settings');
const taskListPermission = checkPermission('task.list');

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    name: "Home",
    meta: { activePath: "/" },
    component: Home,
    beforeEnter: multiguard([ifAuthenticated, projectViewPermission]),
  },

  {
    path: "/profile",
    name: "CustomerProfile",
    meta: { activePath: "profile" },
    component: UserProfile,
    beforeEnter: multiguard([ifAuthenticated, projectViewPermission]),
  },

  {
    path: "/login",
    name: "Login",
    component: Login,
    beforeEnter: ifNotAuthenticated,
  },

  {
    path: "/googleAuth",
    name: "GoogleAuth",
    component: GoogleAuth,
    beforeEnter: ifNotAuthenticated,
  },

  {
    path: "/switch-role",
    name: "SwitchRole",
    component: SwitchRole,
    beforeEnter: ifAuthenticated,
  },

  {
    path: "/app-login",
    name: "AppLogin",
    component: MobileLogin,
    beforeEnter: ifNotAuthenticated,
  },

  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPassword,
    beforeEnter: ifNotAuthenticated,
  }, 

  {
    path: "/projects/create",
    name: "CreateProject",
    component: CreateProject,
    beforeEnter: multiguard([ifAuthenticated, projectsCreatePermission]),
  },

  {
    path: "/employers/create",
    name: "CreateEmployer",
    component: EmployerCreate,
    beforeEnter: multiguard([ifAuthenticated]),
  },

  {
    path: "/roles/create",
    name: "CreateRole",
    component: CreateRole,
    beforeEnter: multiguard([ifAuthenticated]),
  },

  {
    path: "/employers",
    name: "Employers",
    component: Employers,
    beforeEnter: multiguard([ifAuthenticated, projectViewPermission]),
  },

  {
    path: "/employers/edit/:uuid",
    name: "EmployerEdit",
    component: EmployerEdit,
    beforeEnter: multiguard([ifAuthenticated]),
  },

  {
    path: "/roles/edit/:name",
    name: "EditRole",
    component: EditRole,
    beforeEnter: multiguard([ifAuthenticated]),
  },
  

  {
    path: "/projects",
    name: "ProjectsList",
    component: ProjectsList,
    beforeEnter: multiguard([ifAuthenticated, projectViewPermission]),
  },

  {
    path: "/settings",
    name: "Settings",
    component: Settings,
    beforeEnter: multiguard([ifAuthenticated, SettingsPermission]),
  },

  {
    path: "/roles",
    name: "RoleList",
    component: RoleList,
    beforeEnter: multiguard([ifAuthenticated, projectViewPermission]),
  },
  

  {
    path: "/projects/edit/:uuid",
    name: "EditProject",
    component: EditProject,
    beforeEnter: multiguard([ifAuthenticated]),
  },

  {
    path: "/customers",
    name: "Customers",
    component: Customers,
    beforeEnter: multiguard([ifAuthenticated, projectViewPermission]),
  },

  {
    path: "/customer/:uuid/info",
    name: "Customer",
    component: Customer,
    beforeEnter: multiguard([ifAuthenticated, customerInfo]),
  },

  {
    path: "/customers/create",
    name: "CustomerCreate",
    component: CustomerCreate,
    beforeEnter: multiguard([ifAuthenticated]),
  },
  {
    path: "/customers/edit/:uuid",
    name: "CustomerEdit",
    component: CustomerEdit,
    beforeEnter: multiguard([ifAuthenticated]),
  }, 

  {
    path: "/reports",
    name: "Reports",
    component: Reports,
    beforeEnter: multiguard([ifAuthenticated]),
  },
  {
    path: "/project/info/:uuid",
    name: "ProjectInfo",
    component: ProjectInfo,
    beforeEnter: multiguard([ifAuthenticated, projectViewPermission]),
  },
  {
    path: "/task/info/:uuid",
    name: "TaskInfo",
    component: TaskInfo,
    beforeEnter: multiguard([ifAuthenticated, taskInfo]),
  },
  {
    path: "/calendar",
    name: "Calendar",
    component: Calendar,
    beforeEnter: multiguard([ifAuthenticated]),
  },
  {
    path: "/invoices",
    name: "InvoiceList",
    component: InvoiceList,
    beforeEnter: multiguard([ifAuthenticated, invoicePermission]),
  },
  {
    path: "/invoice/create",
    name: "CreateInvoice",
    component: CreateInvoice,
    beforeEnter: multiguard([ifAuthenticated]),
  },
  {
    path: "/invoice/create/:uuid",
    name: "CreateInvoiceProject",
    component: CreateInvoiceProject,
    beforeEnter: multiguard([ifAuthenticated]),
  },
  {
    path: "/invoices",
    name: "InvoicesList",
    component: InvoicesList,
    beforeEnter: multiguard([ifAuthenticated, projectViewPermission]),
  },
  {
    path: "/invoice/edit/:uuid",
    name: "EditInvoice",
    component: EditInvoice,
    beforeEnter: multiguard([ifAuthenticated]),
  },
  {
    path: "/tasks",
    name: "TasksList",
    component: TasksList,
    beforeEnter: multiguard([ifAuthenticated, taskListPermission]),
  },
  {
    path: "/error",
    name: "AccessDenied",
    component: Page403,
    beforeEnter: multiguard([ifAuthenticated]),
  },
  {
    path: "/:catchAll(.*)",
    name: "Not Found",
    component: Page404,
    beforeEnter: multiguard([ifAuthenticated]),
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

export default router;
